<template>
    <div class="vdpComponent text-sm form-group form-group-floating-label" :class="{vdpWithInput: hasInputElement}">
        <input
            v-if="hasInputElement"
            type="text"
            v-bind="inputAttributes"
            :value="inputValue"
            class="input"
            :name="name"
            :class="{ 'placeholder-shown': false }"
            :min="min"
            :max="max"
            :required="required"
            autocomplete="off"
            @input="processUserInput($event.target.value)"
            @focus="open"
            @click="handleOpen"
        >
        <label class="floating-label">
            {{ label }}
        </label>

        <button
            v-if="hasInputElement && inputValue"
            class="vdpClearInput"
            type="button"
            @click="clearInput"
        />
        <transition name="vdp-toggle-calendar">
            <div
                v-if="opened"
                ref="outerWrap"
                class="vdpOuterWrap"
                style="font-size: 10px;"
                :class="[positionClass, {vdpFloating: hasInputElement}]"
                @click="closeViaOverlay"
            >
                <div class="vdpInnerWrap">
                    <header class="vdpHeader">
                        <button
                            class="vdpArrow vdpArrowPrev"
                            :title="prevMonthCaption"
                            type="button"
                            @click="incrementMonth(-1)"
                        >
                            {{ prevMonthCaption }}
                        </button>
                        <button
                            class="vdpArrow vdpArrowNext"
                            type="button"
                            :title="nextMonthCaption"
                            @click="incrementMonth(1)"
                        >
                            {{ nextMonthCaption }}
                        </button>
                        <div class="vdpPeriodControls">
                            <div class="vdpPeriodControl">
                                <button
                                    :key="currentPeriod.month"
                                    :class="directionClass"
                                    type="button"
                                >
                                    {{ months[currentPeriod.month] }}
                                </button>
                                <select v-model="currentPeriod.month">
                                    <option
                                        v-for="(month, index) in months"
                                        :key="month"
                                        :value="index"
                                    >
                                        {{ month }}
                                    </option>
                                </select>
                            </div>
                            <div class="vdpPeriodControl">
                                <button
                                    :key="currentPeriod.year"
                                    :class="directionClass"
                                    type="button"
                                >
                                    {{ currentPeriod.year }}
                                </button>
                                <select v-model="currentPeriod.year">
                                    <option
                                        v-for="year in yearRange"
                                        :key="year"
                                        :value="year"
                                    >
                                        {{ year }}
                                    </option>
                                </select>
                            </div>
                        </div>
                    </header>
                    <table class="vdpTable">
                        <thead>
                            <tr>
                                <th
                                    v-for="weekday in weekdays"
                                    :key="weekday"
                                    class="vdpHeadCell"
                                >
                                    <span class="vdpHeadCellContent">{{ weekday }}</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody
                            :key="currentPeriod.year + '-' + currentPeriod.month"
                            :class="directionClass"
                        >
                            <tr
                                v-for="(week, weekIndex) in currentPeriodDates"
                                :key="weekIndex"
                                class="vdpRow"
                            >
                                <td
                                    v-for="item in week"
                                    :key="item.dateKey"
                                    class="vdpCell"
                                    :class="{
                                        selectable: !item.disabled,
                                        selected: item.selected,
                                        disabled: item.disabled,
                                        today: item.today,
                                        outOfRange: item.outOfRange
                                    }"
                                    :data-id="item.dateKey"
                                    @click="selectDateItem(item)"
                                >
                                    <div class="vdpCellContent">{{ item.date.getDate() }}</div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <div v-if="pickTime && currentTime" class="vdpTimeControls">
                        <span class="vdpTimeCaption">{{ setTimeCaption }}</span>
                        <div class="vdpTimeUnit">
                            <pre><span>{{ currentTime.hoursPadded }}</span><br></pre>
                            <input
                                type="number"
                                pattern="\d*"
                                class="vdpHoursInput"
                                :value="currentTime.hoursPadded"
                                @input.prevent="inputTime('setHours', $event)"
                            >
                        </div>
                        <span v-if="pickMinutes" class="vdpTimeSeparator">:</span>
                        <div v-if="pickMinutes" class="vdpTimeUnit">
                            <pre><span>{{ currentTime.minutesPadded }}</span><br></pre>
                            <input
                                v-if="pickMinutes"
                                type="number"
                                pattern="\d*"
                                class="vdpMinutesInput"
                                :value="currentTime.minutesPadded"
                                @input="inputTime('setMinutes', $event)"
                            >
                        </div>
                        <span v-if="pickSeconds" class="vdpTimeSeparator">:</span>
                        <div v-if="pickSeconds" class="vdpTimeUnit">
                            <pre><span>{{ currentTime.secondsPadded }}</span><br></pre>
                            <input
                                v-if="pickSeconds"
                                type="number"
                                pattern="\d*"
                                class="vdpSecondsInput"
                                :value="currentTime.secondsPadded"
                                @input="inputTime('setSeconds', $event)"
                            >
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
    import DatePick from 'vue-date-pick';

    export default {
        extends: DatePick,
        props: {
            name: {
                type: String,
                required: false,
                default: null,
            },
            label: {
                type: String,
                required: false,
                default: lang('Välj datum','Select date'),
            },
            min: {
                type: String,
                default: null,
            },
            max: {
                type: String,
                default: null,
            },
            required: {
                type: Boolean,
                default: false,
            },
        },
        methods: {
            handleOpen() {
                EventBus.$emit('form-input-changed');
                this.open();
            },
            clearInput() {
                EventBus.$emit('form-input-changed');
                this.clear();
            },
        },
    };
</script>

<style scoped>
    .vdpWithInput {
        @apply .w-full;
    }

    .vdpTimeUnit {
        @apply .w-5xl;
    }

    .vdpClearInput::before {
        line-height: 1.35;
    }
</style>